import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Star, Target, Brain, BarChart3, Crosshair, MapPin, Sparkles,
  Trophy, Users, Shield, Zap, Apple, Smartphone, Download, ChevronRight,
  Award, Cpu, TrendingUp, CheckCircle,
} from 'lucide-react';
import { APP_STORE_URL } from '@/lib/appStoreUrl';

const CONTACT_EMAIL = 'supportclaypulse@gmail.com';

// Core promises pitched at App Store visitors and Apple reviewers.
const HERO_STATS = [
  { value: '8', label: 'disciplines\nsupportées' },
  { value: '7j', label: "d'essai gratuit\nsans carte" },
  { value: 'IA', label: 'Coach intégré\n(Clayo)' },
  { value: '100%', label: 'RGPD\net EU-hosted' },
];

const KEY_FEATURES = [
  {
    icon: Target,
    title: 'Suivi précis de tes sessions',
    desc: "Enregistre chaque plateau tiré, chaque station, chaque cartouche. Skeet, Trap, DTL, Compak, Parcours de chasse, Ball-Trap de campagne — 8 disciplines dédiées.",
  },
  {
    icon: BarChart3,
    title: 'Statistiques détaillées',
    desc: "Moyennes par station, évolution mois par mois, comparatif entre disciplines. Visualise ta progression et identifie tes points faibles instantanément.",
  },
  {
    icon: Brain,
    title: 'Clayo, coach IA personnel',
    desc: "Un assistant intelligent qui analyse tes données réelles pour te donner des conseils précis sur ta technique, ton matériel et ta progression. Basé sur Gemini 3.",
  },
  {
    icon: Crosshair,
    title: 'Gestion de ton arsenal',
    desc: "Enregistre tes fusils, tes chokes, tes cartouches. L'app calcule automatiquement ton stock, tes rotations, et te prévient quand tu dois réapprovisionner.",
  },
  {
    icon: MapPin,
    title: 'Carte des événements & stands',
    desc: "Retrouve tous les Ball-Trap de Campagne, concours, stands de tir et armureries en France sur une carte interactive. Filtre par catégorie et par région.",
  },
  {
    icon: Trophy,
    title: 'Concours et compétitions',
    desc: "Consulte le calendrier des concours à venir, inscris tes résultats et suis tes performances en compétition. Idéal pour les tireurs en club.",
  },
];

const TESTIMONIAL_HIGHLIGHTS = [
  { icon: Star, text: "Interface claire, prise en main immédiate" },
  { icon: Shield, text: "Données 100% privées, jamais partagées" },
  { icon: Zap, text: "Fonctionne hors connexion" },
];

const APP_INFO = [
  { label: 'Éditeur', value: 'ClayPulse' },
  { label: 'Catégorie', value: 'Sport' },
  { label: 'Langue', value: 'Français' },
  { label: 'Âge requis', value: '4+' },
  { label: 'Compatibilité iOS', value: 'iOS 15 et supérieur' },
  { label: 'Compatibilité Android', value: 'Android 8 (Oreo) et supérieur' },
  { label: 'Taille approximative', value: '25 Mo (iOS) / 40 Mo (Android)' },
  { label: 'Prix', value: 'Gratuit avec achats intégrés (6,99 €/mois ou 67 €/an)' },
];

// Slideshow screenshots used in the "In action" gallery.
const SCREENSHOTS = [
  { src: '/assets/img/hero-phone-1.jpg', label: 'Statistiques' },
  { src: '/assets/img/hero-phone-2.jpg', label: 'Carte des stands' },
  { src: '/assets/img/hero-phone-3.jpg', label: 'Stock cartouches' },
  { src: '/assets/img/hero-phone-4.jpg', label: 'Concours' },
  { src: '/assets/img/hero-phone-5.jpg', label: 'Profil tireur' },
  { src: '/assets/img/hero-phone-6.jpg', label: 'Session en cours' },
];

export default function Marketing() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-0">
        <div className="absolute top-40 right-[10%] w-[500px] h-[500px] bg-[#FF6B35]/8 rounded-full blur-[140px]" />
        <div className="absolute bottom-40 left-[10%] w-[400px] h-[400px] bg-[#FFC107]/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/"
          data-testid="marketing-back-home"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-10 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l&apos;accueil
        </Link>

        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-14 sm:mb-20"
          data-testid="marketing-hero"
        >
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-5">
                <Sparkles size={13} className="text-[#FF6B35]" />
                <span className="text-[#FF6B35] text-[11px] font-bold uppercase tracking-[0.2em]">
                  ClayPulse
                </span>
              </div>

              <h1 className="font-['Poppins'] text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5">
                L&apos;app n°1 des{' '}
                <span className="bg-gradient-to-r from-[#FFC107] to-[#FF6B35] bg-clip-text text-transparent">
                  tireurs sportifs
                </span>
              </h1>

              <p className="text-[#C8C8C8] text-base sm:text-lg leading-relaxed mb-6 max-w-2xl">
                ClayPulse est l&apos;application dédiée aux passionnés de ball-trap.
                Enregistre tes sessions, analyse tes performances, gère ton arsenal
                et progresse plus vite avec Clayo, ton coach IA personnel.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                {HERO_STATS.map((s, i) => (
                  <div
                    key={i}
                    className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5"
                  >
                    <div className="text-[#FF6B35] font-black text-xl sm:text-2xl font-['Poppins']">
                      {s.value}
                    </div>
                    <div className="text-[#A0A0A0] text-[11px] whitespace-pre-line leading-tight">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="marketing-ios-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold text-sm hover:shadow-[0_0_25px_rgba(255,107,53,0.4)] transition-all hover:scale-105"
                >
                  <Apple size={17} />
                  Télécharger sur l&apos;App Store
                </a>
                <Link
                  to="/android"
                  data-testid="marketing-android-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.05] border border-white/10 text-white font-semibold text-sm hover:bg-white/[0.08] hover:border-[#FF6B35]/30 transition-all hover:scale-105"
                >
                  <Smartphone size={17} />
                  Version Android
                </Link>
              </div>
            </div>

            {/* Phone mockup */}
            <div className="relative flex justify-center">
              <div className="relative w-[240px] sm:w-[280px] rounded-[42px] border-[8px] border-[#222] bg-[#0a0a0a] shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_60px_rgba(255,107,53,0.15)] overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[90px] h-[24px] bg-black rounded-full z-30" />
                <div className="relative aspect-[9/19] bg-black overflow-hidden">
                  <img
                    src={SCREENSHOTS[0].src}
                    alt="ClayPulse — Statistiques"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/25 rounded-full" />
              </div>
            </div>
          </div>
        </motion.header>

        {/* Screenshot gallery */}
        <section className="mb-14 sm:mb-20" data-testid="marketing-gallery">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
            L&apos;app en action
          </h2>
          <p className="text-[#A0A0A0] text-sm mb-8">
            6 écrans clés pour comprendre en 30 secondes ce que ClayPulse peut faire pour toi.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {SCREENSHOTS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group"
              >
                <div className="rounded-2xl overflow-hidden border-[4px] border-[#1a1a1a] bg-black aspect-[9/19] shadow-lg group-hover:border-[#FF6B35]/30 transition-colors">
                  <img
                    src={s.src}
                    alt={`ClayPulse — ${s.label}`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="text-center text-[11px] uppercase tracking-wider text-[#A0A0A0] font-semibold mt-2">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Key features */}
        <section className="mb-14 sm:mb-20" data-testid="marketing-features">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
            Ce que ClayPulse fait pour toi
          </h2>
          <p className="text-[#A0A0A0] text-sm mb-8">
            Une suite complète pensée pour les tireurs de ball-trap, du débutant au compétiteur.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {KEY_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#FF6B35]/25 hover:bg-white/[0.05] transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/30 flex items-center justify-center mb-3">
                    <Icon size={20} className="text-[#FF6B35]" />
                  </div>
                  <h3 className="text-white font-bold text-base font-['Poppins'] mb-2">
                    {f.title}
                  </h3>
                  <p className="text-[#A0A0A0] text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Trust strip */}
        <section className="mb-14 sm:mb-20 rounded-2xl bg-gradient-to-br from-[#FF6B35]/8 to-transparent border border-[#FF6B35]/20 p-6 sm:p-8">
          <div className="grid sm:grid-cols-3 gap-5">
            {TESTIMONIAL_HIGHLIGHTS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <Icon size={22} className="text-[#FF6B35] shrink-0 mt-0.5" />
                  <p className="text-white text-sm leading-snug font-medium">{t.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Pricing recap */}
        <section className="mb-14 sm:mb-20" data-testid="marketing-pricing">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">Tarifs</h2>
          <p className="text-[#A0A0A0] text-sm mb-6">
            7 jours d&apos;essai gratuit sans carte. Aucune reconduction automatique tant
            que tu ne choisis pas de t&apos;abonner explicitement.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10">
              <div className="text-[#A0A0A0] text-xs uppercase tracking-wider mb-1">Mensuel</div>
              <div className="text-white text-4xl font-black font-['Poppins'] mb-1">
                6,99&nbsp;<span className="text-2xl">€</span>
                <span className="text-sm text-[#A0A0A0] font-normal"> / mois</span>
              </div>
              <p className="text-[#A0A0A0] text-xs">Sans engagement. Annulable à tout moment.</p>
            </div>
            <div className="relative p-6 rounded-2xl bg-gradient-to-br from-[#FF6B35]/12 to-[#FF6B35]/4 border border-[#FF6B35]/40">
              <span className="absolute -top-2.5 left-6 px-2.5 py-0.5 rounded-full bg-[#FF6B35] text-white text-[10px] font-bold uppercase tracking-wider">
                Économies −20%
              </span>
              <div className="text-[#FF6B35] text-xs uppercase tracking-wider mb-1">Annuel</div>
              <div className="text-white text-4xl font-black font-['Poppins'] mb-1">
                67&nbsp;<span className="text-2xl">€</span>
                <span className="text-sm text-[#A0A0A0] font-normal"> / an</span>
              </div>
              <p className="text-[#A0A0A0] text-xs">
                Soit 5,58 €/mois. Facturation unique annuelle.
              </p>
            </div>
          </div>
          <p className="text-[#666] text-xs mt-4">
            Paiements sécurisés par Stripe (PCI-DSS). Gestion et annulation depuis
            l&apos;app à tout moment.
          </p>
        </section>

        {/* Technical highlights */}
        <section className="mb-14 sm:mb-20" data-testid="marketing-tech">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
            Une app pensée pour les vrais tireurs
          </h2>
          <p className="text-[#A0A0A0] text-sm mb-6">
            Une base technique moderne, respectueuse de tes données et pensée pour durer.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Cpu, title: 'IA embarquée', desc: "Coach Clayo alimenté par Google Gemini 3, réponses en moins de 5 secondes." },
              { icon: Shield, title: 'RGPD & confidentialité', desc: "Serveurs européens, aucun partage tiers, export complet de tes données à tout moment." },
              { icon: Zap, title: 'Hors ligne', desc: "Toutes les fonctions principales fonctionnent sans connexion. Synchro dès le retour du réseau." },
              { icon: TrendingUp, title: 'Progrès mesurables', desc: "Statistiques détaillées, comparaisons, tendances mensuelles pour visualiser ta progression." },
            ].map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={16} className="text-[#FF6B35]" />
                    <span className="text-white font-semibold text-sm">{h.title}</span>
                  </div>
                  <p className="text-[#A0A0A0] text-xs leading-relaxed">{h.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* App info card — Apple review-friendly */}
        <section
          className="mb-14 sm:mb-20 rounded-2xl bg-white/[0.02] border border-white/5 p-6 sm:p-8"
          data-testid="marketing-app-info"
        >
          <h2 className="text-xl font-bold font-['Poppins'] mb-5 flex items-center gap-2">
            <Award size={18} className="text-[#FF6B35]" />
            Informations sur l&apos;application
          </h2>
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {APP_INFO.map((row, i) => (
              <div key={i} className="flex items-baseline justify-between border-b border-white/5 pb-2">
                <dt className="text-[#A0A0A0] text-sm">{row.label}</dt>
                <dd className="text-white text-sm font-medium text-right">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Quick FAQ */}
        <section className="mb-14 sm:mb-20" data-testid="marketing-faq">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
            En 3 questions
          </h2>
          <div className="space-y-3 mt-6">
            {[
              {
                q: "Qui est ClayPulse fait pour ?",
                a: "Tous les tireurs sportifs pratiquant le ball-trap (Skeet, Trap, DTL, Compak, Parcours de chasse, Campagne). Du tireur du dimanche au compétiteur.",
              },
              {
                q: "Puis-je essayer avant d'acheter ?",
                a: "Oui, 7 jours d'essai gratuit sans carte bancaire. Aucune reconduction automatique — tu choisis explicitement si tu veux t'abonner à la fin.",
              },
              {
                q: "Mes données sont-elles en sécurité ?",
                a: "Absolument. Serveurs en Europe, chiffrement des mots de passe, conformité RGPD, aucun partage avec des tiers publicitaires. Tu peux exporter ou supprimer tes données à tout moment.",
              },
            ].map((it, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/[0.03] border border-white/5">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle size={16} className="text-[#FF6B35] shrink-0 mt-0.5" />
                  <h3 className="text-white font-semibold text-sm sm:text-base">{it.q}</h3>
                </div>
                <p className="text-[#C8C8C8] text-sm leading-relaxed pl-6">{it.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact & legal */}
        <section
          className="rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] border border-white/10 p-7 sm:p-9 text-center"
          data-testid="marketing-contact"
        >
          <Users size={26} className="text-[#FF6B35] mx-auto mb-3" />
          <h3 className="text-white text-xl font-bold font-['Poppins'] mb-2">
            Une question, une suggestion&nbsp;?
          </h3>
          <p className="text-[#A0A0A0] text-sm mb-5 max-w-md mx-auto">
            L&apos;équipe ClayPulse est joignable directement par email. Réponse
            personnelle sous 24 à 48 h ouvrés.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Contact%20ClayPulse`}
            data-testid="marketing-contact-email"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold text-sm hover:shadow-[0_0_25px_rgba(255,107,53,0.4)] transition-all hover:scale-105"
          >
            {CONTACT_EMAIL}
          </a>

          <div className="flex flex-wrap justify-center gap-2 mt-8 pt-6 border-t border-white/5">
            <Link
              to="/support"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white text-xs font-semibold hover:border-[#FF6B35]/30 transition"
            >
              <ChevronRight size={13} /> Centre d&apos;assistance
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white text-xs font-semibold hover:border-[#FF6B35]/30 transition"
            >
              <ChevronRight size={13} /> Politique de confidentialité
            </Link>
            <Link
              to="/cgu"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white text-xs font-semibold hover:border-[#FF6B35]/30 transition"
            >
              <ChevronRight size={13} /> Conditions d&apos;utilisation
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
